import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTransition } from '../../utils/pageTransitions';

const ForgotPassword = () => {
    const { resetPassword } = useAuth();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const { variants, transition } = usePageTransition();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');

        try {
            await resetPassword(email);
            setMessage('Un email de réinitialisation vous a été envoyé. Vérifiez votre boîte de réception.');
        } catch (err) {
            setError(err.message || 'Erreur lors de l\'envoi de l\'email.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 flex items-center justify-center p-4"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={variants}
            transition={transition}
        >
            <Helmet>
                <title>Mot de passe oublié - ORAZEK Investment</title>
            </Helmet>
            <div className="bg-card rounded-2xl shadow-premium-xl w-full max-w-md border border-border">
                <div className="p-6 md:p-8">
                    <div className="flex items-center justify-center mb-8">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                <Icon name="KeyRound" size={32} color="var(--color-primary)" />
                            </div>
                            <h1 className="text-2xl font-bold text-foreground mb-2">Mot de passe oublié ?</h1>
                            <p className="text-sm text-muted-foreground">Entrez votre email pour recevoir les instructions.</p>
                        </div>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-2 text-error text-sm">
                            <Icon name="AlertCircle" size={20} className="flex-shrink-0 mt-0.5" />
                            <p>{error}</p>
                        </div>
                    )}

                    {message && (
                        <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-2 text-success text-sm">
                            <Icon name="CheckCircle" size={20} className="flex-shrink-0 mt-0.5" />
                            <p>{message}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <Input
                            type="email"
                            label="Adresse Email"
                            placeholder="admin@orazek.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />

                        <Button
                            type="submit"
                            variant="default"
                            fullWidth
                            disabled={loading}
                            iconName="Send"
                            iconPosition="right"
                        >
                            {loading ? 'Envoi en cours...' : 'Envoyer les instructions'}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <Link to="/admin-login" className="text-sm text-primary hover:underline flex items-center justify-center gap-1">
                            <Icon name="ArrowLeft" size={14} />
                            Retour à la connexion
                        </Link>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default ForgotPassword;
